import React, { useEffect, useState, useRef, Fragment } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MoreVertical, X, Filter, ChevronDown, Plus, Sparkles, ThumbsUp, ThumbsDown, Info, ArrowUpDown, Loader2, Layers, MapPin, Upload, Settings, Eye, EyeOff, FileSpreadsheet, List, MousePointer as MousePointerIcon, CheckSquare as CheckSquareIcon } from 'lucide-react';
import { LensVersion } from '../types';
const TABLE_DATA = [{
  state: 'California',
  totalHH: '1,420,000',
  customers: '68,900',
  avgNW: '$265.4K',
  penetration: '4.9%'
}, {
  state: 'New York',
  totalHH: '214,400',
  customers: '40,231',
  avgNW: '$97.6M',
  penetration: '18.8%'
}, {
  state: 'Florida',
  totalHH: '198,200',
  customers: '40,231',
  avgNW: '$96.5M',
  penetration: '20.3%'
}, {
  state: 'Texas',
  totalHH: '220,000',
  customers: '40,231',
  avgNW: '$95.4M',
  penetration: '0.2%'
}, {
  state: 'New Jersey',
  totalHH: '88,900',
  customers: '3,467',
  avgNW: '$94.3M',
  penetration: '3.9%'
}, {
  state: 'Ohio',
  totalHH: '86,200',
  customers: '3,189',
  avgNW: '$93.2M',
  penetration: '3.7%'
}, {
  state: 'Washington',
  totalHH: '95,200',
  customers: '2,950',
  avgNW: '$92.1M',
  penetration: '3.1%'
}, {
  state: 'Illinois',
  totalHH: '112,300',
  customers: '4,268',
  avgNW: '$91.0M',
  penetration: '3.2%'
}, {
  state: 'Pennsylvania',
  totalHH: '104,500',
  customers: '3,657',
  avgNW: '$91.2K',
  penetration: '3.5%'
}, {
  state: 'Arizona',
  totalHH: '74,600',
  customers: '3,059',
  avgNW: '$90.1K',
  penetration: '4.1%'
}];
export function MarketLensDashboard({
  versions,
  activeVersionId,
  onSelectVersion,
  onNewLens,
  onClose
}: {
  versions: LensVersion[];
  activeVersionId: string | null;
  onSelectVersion: (id: string) => void;
  onNewLens: () => void;
  onClose?: () => void;
}) {
  const [myCustomersEnabled, setMyCustomersEnabled] = useState(true);
  const [breakdownView, setBreakdownView] = useState<'State' | 'County'>('State');
  const [selectedStates, setSelectedStates] = useState<Set<string>>(new Set());
  const [showToast, setShowToast] = useState(false);
  const activeLens = versions.find(v => v.id === activeVersionId);
  // My Locations state
  const [locationSolution, setLocationSolution] = useState<1 | 2 | 3 | 4 | 5>(1);
  const [hasLocations, setHasLocations] = useState(false);
  const [locationsEnabled, setLocationsEnabled] = useState(false);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [showUploadPrompt, setShowUploadPrompt] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  // Select Mode state
  const [selectModeSolution, setSelectModeSolution] = useState<1 | 2 | 3 | 4 | 5>(1);
  const [selectModeActive, setSelectModeActive] = useState(false);
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowLocationDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const handleToggleLocations = () => {
    if (!hasLocations) {
      setShowUploadPrompt(true);
    } else {
      setLocationsEnabled(!locationsEnabled);
    }
  };
  const handleSimulateUpload = () => {
    setHasLocations(true);
    setLocationsEnabled(true);
    setShowUploadPrompt(false);
    setShowLocationDropdown(false);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };
  const toggleState = (state: string) => {
    setSelectedStates(prev => {
      const next = new Set(prev);
      if (next.has(state)) next.delete(state);else next.add(state);
      return next;
    });
  };
  const toggleAll = () => {
    if (selectedStates.size === TABLE_DATA.length) setSelectedStates(new Set());else setSelectedStates(new Set(TABLE_DATA.map(r => r.state)));
  };
  const handleGenerateSegment = () => {
    setShowToast(true);
    setTimeout(() => setShowToast(false), 4000);
  };
  // Render the My Locations control based on selected solution
  const renderMyLocations = () => {
    switch (locationSolution) {
      // Solution 1: Toggle + Inline Empty State
      case 1:
        return <div className="flex items-center space-x-3" data-id="element-354">
            <span className="text-sm text-gray-700" data-id="element-355">My Locations</span>
            <button onClick={handleToggleLocations} className={`w-11 h-6 rounded-full transition-colors relative ${locationsEnabled ? 'bg-teal-500' : 'bg-gray-200'}`} data-id="element-356">
              <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${locationsEnabled ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-357" />
            </button>
            {hasLocations && <span className="text-xs text-gray-400 font-medium" data-id="element-358">12 pins</span>}
          </div>;
      // Solution 2: Status Chip
      case 2:
        return <button onClick={() => hasLocations ? setLocationsEnabled(!locationsEnabled) : setShowUploadPrompt(true)} className={`flex items-center space-x-2 px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${hasLocations ? locationsEnabled ? 'bg-teal-50 border-teal-200 text-teal-700 hover:bg-teal-100' : 'bg-gray-50 border-gray-200 text-gray-500 hover:bg-gray-100' : 'bg-white border-dashed border-gray-300 text-gray-500 hover:border-teal-400 hover:text-teal-600'}`} data-id="element-359">
            <MapPin size={14} data-id="element-360" />
            <span data-id="element-361">
              {hasLocations ? locationsEnabled ? '12 locations' : '12 locations (hidden)' : 'No locations'}
            </span>
            {!hasLocations && <Plus size={12} data-id="element-362" />}
          </button>;
      // Solution 3: Split Toggle + Gear
      case 3:
        return <div className="flex items-center space-x-2" data-id="element-363">
            <span className="text-sm text-gray-700" data-id="element-364">My Locations</span>
            <button onClick={handleToggleLocations} className={`w-11 h-6 rounded-full transition-colors relative ${locationsEnabled ? 'bg-teal-500' : 'bg-gray-200'}`} data-id="element-365">
              <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${locationsEnabled ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-366" />
            </button>
            <button onClick={() => setShowUploadPrompt(true)} className="p-1.5 text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded-md transition-colors" title="Manage locations" data-id="element-367">
              <Settings size={14} data-id="element-368" />
            </button>
          </div>;
      // Solution 4: Dropdown Menu
      case 4:
        return <div className="relative" ref={dropdownRef} data-id="element-369">
            <button onClick={() => setShowLocationDropdown(!showLocationDropdown)} className="flex items-center space-x-2 px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-370">
              <MapPin size={14} className="text-gray-500" data-id="element-371" />
              <span data-id="element-372">My Locations</span>
              {hasLocations && <span className="bg-teal-100 text-teal-700 text-xs font-bold px-1.5 py-0.5 rounded-full" data-id="element-373">
                  12
                </span>}
              <ChevronDown size={14} className={`text-gray-400 transition-transform ${showLocationDropdown ? 'rotate-180' : ''}`} data-id="element-374" />
            </button>
            <AnimatePresence data-id="element-375">
              {showLocationDropdown && <motion.div initial={{
              opacity: 0,
              y: -4,
              scale: 0.97
            }} animate={{
              opacity: 1,
              y: 0,
              scale: 1
            }} exit={{
              opacity: 0,
              y: -4,
              scale: 0.97
            }} transition={{
              duration: 0.15
            }} className="absolute right-0 top-full mt-1 w-52 bg-white border border-gray-200 rounded-lg shadow-lg z-20 overflow-hidden" data-id="element-376">
                  <button onClick={handleSimulateUpload} className="w-full flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-377">
                    <Upload size={14} className="mr-3 text-gray-400" data-id="element-378" />
                    Upload CSV
                  </button>
                  {hasLocations && <>
                      <button onClick={() => {
                  setLocationsEnabled(!locationsEnabled);
                  setShowLocationDropdown(false);
                }} className="w-full flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-379">
                        {locationsEnabled ? <EyeOff size={14} className="mr-3 text-gray-400" data-id="element-380" /> : <Eye size={14} className="mr-3 text-gray-400" data-id="element-381" />}
                        {locationsEnabled ? 'Hide on Map' : 'Show on Map'}
                      </button>
                      <button onClick={() => setShowLocationDropdown(false)} className="w-full flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors border-t border-gray-100" data-id="element-382">
                        <List size={14} className="mr-3 text-gray-400" data-id="element-383" />
                        Manage Locations
                      </button>
                    </>}
                </motion.div>}
            </AnimatePresence>
          </div>;
      // Solution 5: Upload Pill with Counter
      case 5:
        return hasLocations ? <button onClick={() => setLocationsEnabled(!locationsEnabled)} className={`flex items-center space-x-2 pl-2 pr-3 py-1.5 rounded-full text-sm font-medium transition-all ${locationsEnabled ? 'bg-teal-500 text-white hover:bg-teal-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-384">
            <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${locationsEnabled ? 'bg-white/20 text-white' : 'bg-gray-300 text-white'}`} data-id="element-385">
              12
            </div>
            <MapPin size={14} data-id="element-386" />
            <span data-id="element-387">Locations</span>
          </button> : <button onClick={() => setShowUploadPrompt(true)} className="flex items-center space-x-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-white border border-dashed border-gray-300 text-gray-500 hover:border-teal-400 hover:text-teal-600 hover:bg-teal-50 transition-all" data-id="element-388">
            <Upload size={13} data-id="element-389" />
            <span data-id="element-390">Add Locations</span>
          </button>;
    }
  };
  return <div className="flex-1 overflow-y-auto bg-white p-6 relative flex flex-col" data-id="element-391">
      <div className="max-w-5xl mx-auto w-full" data-id="element-392">
        <div className="flex items-center justify-between mb-4" data-id="element-393">
          <div data-id="element-394">
            <div className="flex items-center space-x-2 mb-1" data-id="element-395">
              <div className="w-5 h-5 bg-teal-500 rounded flex items-center justify-center text-white font-bold text-xs" data-id="element-396">
                W
              </div>
              <h2 className="text-lg font-bold text-gray-900 tracking-wide" data-id="element-397">
                MARKET INSIGHTS LABS
              </h2>
            </div>
            <p className="text-xs text-gray-500" data-id="element-398">
              Generated at:{' '}
              <span className="font-medium text-gray-900" data-id="element-399">
                {new Date().toLocaleString()}
              </span>
            </p>
          </div>
          <div className="flex items-center space-x-2 text-gray-400" data-id="element-400">
            <button className="p-1.5 hover:bg-gray-100 rounded-md transition-colors" data-id="element-401">
              <MoreVertical size={18} data-id="element-402" />
            </button>
            <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-md transition-colors" data-id="element-403">
              <X size={18} data-id="element-404" />
            </button>
          </div>
        </div>

        {/* Tab Bar for Lens Versions */}
        <div className="flex items-center space-x-1 border-b border-gray-200 mb-6 overflow-x-auto hide-scrollbar" data-id="element-405">
          {versions.map(version => {
          const isActive = version.id === activeVersionId;
          return <button key={version.id} onClick={() => onSelectVersion(version.id)} className={`relative px-4 py-2.5 text-sm font-medium transition-colors whitespace-nowrap ${isActive ? 'text-teal-700' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`} data-id="element-406">
                {version.name}
                {isActive && <motion.div layoutId="activeTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-teal-500" initial={false} transition={{
              type: 'spring',
              stiffness: 500,
              damping: 30
            }} data-id="element-407" />}
              </button>;
        })}
          <button onClick={onNewLens} className="px-4 py-2.5 text-sm font-medium text-gray-400 hover:text-teal-600 transition-colors whitespace-nowrap flex items-center" data-id="element-408">
            <Plus size={14} className="mr-1" data-id="element-409" /> New Lens
          </button>
        </div>

        <div className="bg-[#F8F9FA] border border-indigo-100 rounded-lg p-3 flex items-center text-sm mb-6" data-id="element-410">
          <Filter size={16} className="text-indigo-500 mr-3 shrink-0" data-id="element-411" />
          <div className="flex-1 flex flex-wrap items-center gap-2 text-indigo-600" data-id="element-412">
            {activeLens?.lensData.netWorth.min && <span data-id="element-413">{activeLens.lensData.netWorth.min} + net worth</span>}
            {activeLens?.lensData.netWorth.min && <span className="text-indigo-300" data-id="element-414">•</span>}
            {activeLens?.lensData.assets.max && <span data-id="element-415">≤{activeLens.lensData.assets.max} investable assets</span>}
            {activeLens?.lensData.assets.max && <span className="text-indigo-300" data-id="element-416">•</span>}
            {activeLens?.lensData.states.map(state => <Fragment key={state} data-id="element-417">
                <span data-id="element-418">{state}</span>
                <span className="text-indigo-300" data-id="element-419">•</span>
              </Fragment>)}
            {activeLens?.lensData.careers.map(career => <Fragment key={career} data-id="element-420">
                <span data-id="element-421">{career}</span>
                <span className="text-indigo-300" data-id="element-422">•</span>
              </Fragment>)}
            <button className="underline hover:text-indigo-800" data-id="element-423">Edit</button>
          </div>
        </div>

        {/* Solution Picker */}
        <div className="flex items-center gap-2 mb-4 p-3 bg-indigo-50/70 border border-indigo-100 rounded-lg" data-id="element-424">
          <span className="text-xs font-semibold text-indigo-600 mr-2 whitespace-nowrap" data-id="element-425">
            🧪 My Locations UX:
          </span>
          {([1, 2, 3, 4, 5] as const).map(n => <button key={n} onClick={() => {
          setLocationSolution(n);
          setShowUploadPrompt(false);
          setShowLocationDropdown(false);
        }} className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${locationSolution === n ? 'bg-indigo-600 text-white' : 'bg-white text-indigo-600 border border-indigo-200 hover:bg-indigo-100'}`} data-id="element-426">
              {n}
            </button>)}
          <span className="text-[10px] text-indigo-400 ml-2" data-id="element-427">
            {locationSolution === 1 && 'Toggle + Inline Empty State'}
            {locationSolution === 2 && 'Status Chip'}
            {locationSolution === 3 && 'Split Toggle + Gear'}
            {locationSolution === 4 && 'Dropdown Menu'}
            {locationSolution === 5 && 'Upload Pill with Counter'}
          </span>
          <div className="ml-auto flex items-center gap-2" data-id="element-428">
            <button onClick={() => {
            setHasLocations(false);
            setLocationsEnabled(false);
            setShowUploadPrompt(false);
          }} className="text-[10px] text-indigo-500 hover:text-indigo-700 underline" data-id="element-429">
              Reset (no data)
            </button>
            <button onClick={() => {
            setHasLocations(true);
            setLocationsEnabled(true);
            setShowUploadPrompt(false);
          }} className="text-[10px] text-indigo-500 hover:text-indigo-700 underline" data-id="element-430">
              Simulate upload
            </button>
          </div>
        </div>

        <div className="flex items-center justify-between mb-6" data-id="element-431">
          <button className="flex items-center justify-between w-48 px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 hover:bg-gray-50" data-id="element-432">
            All Regions <ChevronDown size={16} className="text-gray-400" data-id="element-433" />
          </button>

          <div className="flex items-center space-x-6" data-id="element-434">
            <div className="flex items-center space-x-3" data-id="element-435">
              <span className="text-sm text-gray-700" data-id="element-436">My Customers</span>
              <button onClick={() => setMyCustomersEnabled(!myCustomersEnabled)} className={`w-11 h-6 rounded-full transition-colors relative ${myCustomersEnabled ? 'bg-teal-500' : 'bg-gray-200'}`} data-id="element-437">
                <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${myCustomersEnabled ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-438" />
              </button>
            </div>
            {renderMyLocations()}
          </div>
        </div>

        {/* Inline Upload Prompt (for solutions 1, 2, 3, 5) */}
        <AnimatePresence data-id="element-439">
          {showUploadPrompt && <motion.div initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} transition={{
          duration: 0.2
        }} className="overflow-hidden mb-6" data-id="element-440">
              <div className="bg-gray-50 border border-gray-200 border-dashed rounded-xl p-5" data-id="element-441">
                <div className="flex items-start justify-between" data-id="element-442">
                  <div className="flex items-start space-x-4" data-id="element-443">
                    <div className="w-10 h-10 bg-teal-50 rounded-lg flex items-center justify-center shrink-0" data-id="element-444">
                      <FileSpreadsheet size={20} className="text-teal-600" data-id="element-445" />
                    </div>
                    <div data-id="element-446">
                      <h4 className="text-sm font-semibold text-gray-900 mb-1" data-id="element-447">
                        Upload your office locations
                      </h4>
                      <p className="text-xs text-gray-500 mb-3" data-id="element-448">
                        Upload a CSV with your branch or office addresses to see
                        them on the map alongside market data.
                      </p>
                      <div className="flex items-center gap-2" data-id="element-449">
                        <button onClick={handleSimulateUpload} className="flex items-center px-3 py-1.5 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium rounded-lg transition-colors" data-id="element-450">
                          <Upload size={14} className="mr-1.5" data-id="element-451" />
                          Upload CSV
                        </button>
                        <button onClick={() => setShowUploadPrompt(false)} className="px-3 py-1.5 text-gray-500 hover:text-gray-700 text-sm font-medium transition-colors" data-id="element-452">
                          Dismiss
                        </button>
                      </div>
                    </div>
                  </div>
                  <button onClick={() => setShowUploadPrompt(false)} className="p-1 text-gray-400 hover:text-gray-600 transition-colors" data-id="element-453">
                    <X size={14} data-id="element-454" />
                  </button>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>

        {/* Select Mode Solution Picker */}
        <div className="flex items-center gap-2 mb-4 p-3 bg-amber-50/70 border border-amber-100 rounded-lg" data-id="element-455">
          <span className="text-xs font-semibold text-amber-700 mr-2 whitespace-nowrap" data-id="element-456">
            🧪 Select Mode UX:
          </span>
          {([1, 2, 3, 4, 5] as const).map(n => <button key={n} onClick={() => {
          setSelectModeSolution(n);
          setSelectModeActive(false);
        }} className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${selectModeSolution === n ? 'bg-amber-600 text-white' : 'bg-white text-amber-700 border border-amber-200 hover:bg-amber-100'}`} data-id="element-457">
              {n}
            </button>)}
          <span className="text-[10px] text-amber-500 ml-2" data-id="element-458">
            {selectModeSolution === 1 && 'Floating Map Toolbar'}
            {selectModeSolution === 2 && 'Toggle Pill + Border Glow'}
            {selectModeSolution === 3 && 'Map Header Mode Bar'}
            {selectModeSolution === 4 && 'Contextual Hint (⇧ Click)'}
            {selectModeSolution === 5 && 'Slide-Up Selection Tray'}
          </span>
        </div>

        {/* Map Container */}
        <div className={`border rounded-xl mb-6 relative bg-white overflow-hidden transition-all duration-300 ${selectModeActive && selectModeSolution === 2 ? 'border-teal-400 ring-2 ring-teal-100' : selectModeActive && selectModeSolution !== 4 ? 'border-teal-300' : 'border-gray-200'}`} data-id="element-459">
          {/* Solution 3: Map Header Mode Bar */}
          {selectModeSolution === 3 && <div className={`flex items-center justify-between px-4 py-2.5 border-b transition-colors duration-200 ${selectModeActive ? 'bg-teal-50 border-teal-200' : 'bg-gray-50 border-gray-200'}`} data-id="element-460">
              <div className="flex items-center bg-white border border-gray-200 rounded-lg p-0.5" data-id="element-461">
                <button onClick={() => setSelectModeActive(false)} className={`flex items-center px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${!selectModeActive ? 'bg-gray-100 text-gray-900' : 'text-gray-500 hover:bg-gray-50'}`} data-id="element-462">
                  <MousePointerIcon size={12} className="mr-1.5" data-id="element-463" />
                  Explore
                </button>
                <button onClick={() => setSelectModeActive(true)} className={`flex items-center px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${selectModeActive ? 'bg-teal-500 text-white' : 'text-gray-500 hover:bg-gray-50'}`} data-id="element-464">
                  <CheckSquareIcon size={12} className="mr-1.5" data-id="element-465" />
                  Select States
                </button>
              </div>
              <AnimatePresence data-id="element-466">
                {selectModeActive && selectedStates.size > 0 && <motion.div initial={{
              opacity: 0,
              x: 10
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: 10
            }} className="flex items-center space-x-3" data-id="element-467">
                    <span className="text-xs font-medium text-teal-700" data-id="element-468">
                      {selectedStates.size} selected
                    </span>
                    <button onClick={handleGenerateSegment} className="flex items-center bg-teal-600 hover:bg-teal-700 text-white text-xs font-medium px-3 py-1.5 rounded-md transition-colors" data-id="element-469">
                      <Layers size={12} className="mr-1.5" data-id="element-470" />
                      Generate Segment
                    </button>
                  </motion.div>}
              </AnimatePresence>
            </div>}

          <div className="p-4 relative" data-id="element-471">
            <div className="w-full aspect-[16/9] relative rounded-lg overflow-hidden flex items-center justify-center bg-gray-50" data-id="element-472">
              <img src="https://cdn.magicpatterns.com/uploads/8A7cxWuKGYjPJkzcPCNcGs/Market_Details_Dash.jpg" alt="Market Map" className={`w-full h-full object-cover object-top absolute inset-0 mix-blend-multiply transition-opacity duration-200 ${selectModeActive ? 'opacity-75' : 'opacity-90'}`} style={{
              clipPath: 'inset(18% 5% 40% 5%)'
            }} data-id="element-473" />

              {/* Select mode overlay tint */}
              {selectModeActive && selectModeSolution !== 4 && <div className="absolute inset-0 bg-teal-500/5 pointer-events-none z-10" data-id="element-474" />}

              {/* Solution 1: Floating Map Toolbar */}
              {selectModeSolution === 1 && <div className="absolute top-3 left-3 z-20" data-id="element-475">
                  <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-1 flex flex-col space-y-0.5" data-id="element-476">
                    <button onClick={() => setSelectModeActive(false)} title="Explore mode — click states for details" className={`p-2 rounded-md transition-colors ${!selectModeActive ? 'bg-gray-100 text-gray-900' : 'text-gray-400 hover:bg-gray-50 hover:text-gray-700'}`} data-id="element-477">
                      <MousePointerIcon size={16} data-id="element-478" />
                    </button>
                    <div className="w-full h-px bg-gray-100" data-id="element-479" />
                    <button onClick={() => setSelectModeActive(true)} title="Select mode — click states to select for segments" className={`p-2 rounded-md transition-colors ${selectModeActive ? 'bg-teal-500 text-white' : 'text-gray-400 hover:bg-gray-50 hover:text-gray-700'}`} data-id="element-480">
                      <CheckSquareIcon size={16} data-id="element-481" />
                    </button>
                  </div>
                  <AnimatePresence data-id="element-482">
                    {selectModeActive && <motion.div initial={{
                  opacity: 0,
                  y: -4
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: -4
                }} className="mt-2 bg-teal-600 text-white text-[10px] font-medium px-2.5 py-1 rounded-md shadow-md whitespace-nowrap" data-id="element-483">
                        Click states to select
                      </motion.div>}
                  </AnimatePresence>
                </div>}

              {/* Solution 2: Toggle Pill in Map Corner */}
              {selectModeSolution === 2 && <div className="absolute top-3 right-3 z-20" data-id="element-484">
                  <button onClick={() => setSelectModeActive(!selectModeActive)} className={`flex items-center space-x-2 px-3 py-2 rounded-full text-xs font-semibold shadow-lg border transition-all duration-200 ${selectModeActive ? 'bg-teal-500 text-white border-teal-600 hover:bg-teal-600' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'}`} data-id="element-485">
                    {selectModeActive ? <CheckSquareIcon size={13} data-id="element-486" /> : <MousePointerIcon size={13} data-id="element-487" />}
                    <span data-id="element-488">{selectModeActive ? 'Selecting' : 'Browse'}</span>
                    <div className={`w-8 h-4 rounded-full relative transition-colors ${selectModeActive ? 'bg-teal-300' : 'bg-gray-200'}`} data-id="element-489">
                      <div className={`absolute top-0.5 w-3 h-3 rounded-full bg-white shadow transition-transform ${selectModeActive ? 'translate-x-4' : 'translate-x-0.5'}`} data-id="element-490" />
                    </div>
                  </button>
                </div>}

              {/* Solution 4: Contextual Hint (Shift+Click) */}
              {selectModeSolution === 4 && <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20" data-id="element-491">
                  <AnimatePresence data-id="element-492">
                    {!selectModeActive ? <motion.div key="hint" initial={{
                  opacity: 0,
                  y: 8
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: 8
                }} className="bg-[#2B3648]/90 backdrop-blur-sm text-white text-xs px-4 py-2 rounded-full shadow-lg flex items-center space-x-2" data-id="element-493">
                        <kbd className="bg-white/20 px-1.5 py-0.5 rounded text-[10px] font-mono" data-id="element-494">
                          ⇧ Shift
                        </kbd>
                        <span data-id="element-495">+ click states to select</span>
                        <span className="text-gray-400 mx-1" data-id="element-496">or</span>
                        <button onClick={() => setSelectModeActive(true)} className="underline underline-offset-2 text-teal-300 hover:text-teal-200" data-id="element-497">
                          Enter Select Mode
                        </button>
                      </motion.div> : <motion.div key="active" initial={{
                  opacity: 0,
                  y: 8
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: 8
                }} className="bg-teal-600/90 backdrop-blur-sm text-white text-xs px-4 py-2 rounded-full shadow-lg flex items-center space-x-3" data-id="element-498">
                        <CheckSquareIcon size={12} data-id="element-499" />
                        <span data-id="element-500">Select mode active — click states</span>
                        <button onClick={() => setSelectModeActive(false)} className="underline underline-offset-2 text-teal-200 hover:text-white" data-id="element-501">
                          Exit
                        </button>
                      </motion.div>}
                  </AnimatePresence>
                </div>}
            </div>

            {/* Solution 5: Slide-Up Selection Tray */}
            {selectModeSolution === 5 && <div className="mt-3" data-id="element-502">
                {!selectModeActive ? <button onClick={() => setSelectModeActive(true)} className="w-full flex items-center justify-center py-2.5 border border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:border-teal-400 hover:text-teal-600 hover:bg-teal-50/50 transition-colors" data-id="element-503">
                    <CheckSquareIcon size={14} className="mr-2" data-id="element-504" />
                    Select states to generate a segment
                  </button> : <AnimatePresence data-id="element-505">
                    <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} className="overflow-hidden" data-id="element-506">
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3" data-id="element-507">
                        <div className="flex items-center justify-between mb-2.5" data-id="element-508">
                          <span className="text-xs font-semibold text-gray-700" data-id="element-509">
                            Select states for segment
                          </span>
                          <button onClick={() => {
                      setSelectModeActive(false);
                      setSelectedStates(new Set());
                    }} className="text-xs text-gray-400 hover:text-gray-600 transition-colors" data-id="element-510">
                            <X size={14} data-id="element-511" />
                          </button>
                        </div>
                        <div className="flex flex-wrap gap-1.5 mb-3" data-id="element-512">
                          {TABLE_DATA.map(row => {
                      const isSelected = selectedStates.has(row.state);
                      return <button key={row.state} onClick={() => toggleState(row.state)} className={`px-2.5 py-1 rounded-md text-xs font-medium border transition-colors ${isSelected ? 'bg-teal-500 text-white border-teal-600' : 'bg-white text-gray-600 border-gray-200 hover:border-teal-300'}`} data-id="element-513">
                                {row.state}
                              </button>;
                    })}
                        </div>
                        {selectedStates.size > 0 && <div className="flex items-center justify-between pt-2.5 border-t border-gray-200" data-id="element-514">
                            <span className="text-xs text-gray-500" data-id="element-515">
                              {selectedStates.size} state
                              {selectedStates.size > 1 ? 's' : ''} selected
                            </span>
                            <div className="flex items-center gap-2" data-id="element-516">
                              <button onClick={() => setSelectedStates(new Set())} className="text-xs text-gray-500 hover:text-gray-700 transition-colors" data-id="element-517">
                                Clear
                              </button>
                              <button onClick={handleGenerateSegment} className="flex items-center bg-teal-600 hover:bg-teal-700 text-white text-xs font-medium px-3 py-1.5 rounded-md transition-colors" data-id="element-518">
                                <Layers size={12} className="mr-1.5" data-id="element-519" />
                                Generate Segment
                              </button>
                            </div>
                          </div>}
                      </div>
                    </motion.div>
                  </AnimatePresence>}
              </div>}

            {/* Bottom row: legends + select mode for solutions 1, 2 (no extra UI needed below) */}
            <div className="flex items-center justify-between mt-4" data-id="element-520">
              {/* Original checkbox for reference — only show if no solution active */}
              <div data-id="element-521" />
              <div className="flex items-center space-x-8 text-xs text-gray-600" data-id="element-522">
                <div className="flex items-center" data-id="element-523">
                  <span className="mr-2" data-id="element-524">Customer Density</span>
                  <div className="flex h-3 w-32 rounded overflow-hidden" data-id="element-525">
                    <div className="flex-1 bg-[#F5E6E6]" data-id="element-526"></div>
                    <div className="flex-1 bg-[#E8C4C4]" data-id="element-527"></div>
                    <div className="flex-1 bg-[#D99E9E]" data-id="element-528"></div>
                    <div className="flex-1 bg-[#C97575]" data-id="element-529"></div>
                    <div className="flex-1 bg-[#B84C4C]" data-id="element-530"></div>
                  </div>
                </div>
                <div className="flex items-center" data-id="element-531">
                  <span className="mr-2" data-id="element-532">TAM Density</span>
                  <div className="flex h-3 w-32 rounded overflow-hidden" data-id="element-533">
                    <div className="flex-1 bg-[#E0F2FE]" data-id="element-534"></div>
                    <div className="flex-1 bg-[#BAE6FD]" data-id="element-535"></div>
                    <div className="flex-1 bg-[#7DD3FC]" data-id="element-536"></div>
                    <div className="flex-1 bg-[#38BDF8]" data-id="element-537"></div>
                    <div className="flex-1 bg-[#0284C7]" data-id="element-538"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-[#F0F9F9] border border-teal-100 rounded-xl p-5 mb-8" data-id="element-539">
          <div className="flex items-start justify-between mb-3" data-id="element-540">
            <div className="flex items-center text-teal-700 font-semibold" data-id="element-541">
              <Sparkles size={18} className="mr-2" data-id="element-542" />
              Market Shift Summary
            </div>
            <div className="flex space-x-2 text-gray-400" data-id="element-543">
              <button className="p-1 hover:text-teal-600 hover:bg-teal-50 rounded transition-colors" data-id="element-544">
                <ThumbsUp size={16} data-id="element-545" />
              </button>
              <button className="p-1 hover:text-red-600 hover:bg-red-50 rounded transition-colors" data-id="element-546">
                <ThumbsDown size={16} data-id="element-547" />
              </button>
            </div>
          </div>
          <div className="text-gray-700 text-sm pl-6" data-id="element-548">
            <p className="mb-2" data-id="element-549">
              You've built strong coverage in key high-density markets, with
              standout performance in:
            </p>
            <ul className="list-disc pl-5 space-y-1 mb-2" data-id="element-550">
              <li data-id="element-551">California — 80,321 customers with 28.1% penetration</li>
              <li data-id="element-552">
                New York & Florida — consistent traction with 18–20%
                penetration...{' '}
                <button className="text-teal-600 hover:underline" data-id="element-553">
                  Read more
                </button>
              </li>
            </ul>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-6 mb-10" data-id="element-554">
          <div className="border-l-4 border-teal-500 pl-4 py-1" data-id="element-555">
            <div className="flex items-center justify-between mb-1" data-id="element-556">
              <h3 className="text-2xl font-bold text-gray-900" data-id="element-557">1.45M</h3>
              <Info size={14} className="text-gray-400" data-id="element-558" />
            </div>
            <p className="text-xs text-gray-500" data-id="element-559">Total TAM Households</p>
          </div>
          <div className="border-l-4 border-teal-200 pl-4 py-1" data-id="element-560">
            <div className="flex items-center justify-between mb-1" data-id="element-561">
              <h3 className="text-2xl font-bold text-gray-900" data-id="element-562">18,200</h3>
              <Info size={14} className="text-gray-400" data-id="element-563" />
            </div>
            <p className="text-xs text-gray-500" data-id="element-564">Matched Customers</p>
          </div>
          <div className="border-l-4 border-blue-200 pl-4 py-1" data-id="element-565">
            <div className="flex items-center justify-between mb-1" data-id="element-566">
              <h3 className="text-2xl font-bold text-gray-900" data-id="element-567">1.25%</h3>
              <Info size={14} className="text-gray-400" data-id="element-568" />
            </div>
            <p className="text-xs text-gray-500" data-id="element-569">Overall Penetration Rate</p>
          </div>
        </div>

        <div data-id="element-570">
          <div className="flex items-center justify-between mb-4" data-id="element-571">
            <h3 className="text-lg font-bold text-gray-900" data-id="element-572">Breakdown</h3>
            <div className="flex bg-white border border-gray-200 rounded-lg p-0.5" data-id="element-573">
              <button onClick={() => setBreakdownView('State')} className={`px-4 py-1.5 text-sm rounded-md transition-colors ${breakdownView === 'State' ? 'bg-teal-50 text-teal-700 font-medium' : 'text-gray-600 hover:bg-gray-50'}`} data-id="element-574">
                State
              </button>
              <button onClick={() => setBreakdownView('County')} className={`px-4 py-1.5 text-sm rounded-md transition-colors ${breakdownView === 'County' ? 'bg-teal-50 text-teal-700 font-medium' : 'text-gray-600 hover:bg-gray-50'}`} data-id="element-575">
                County
              </button>
            </div>
          </div>

          <div className="overflow-x-auto" data-id="element-576">
            <table className="w-full text-sm text-left" data-id="element-577">
              <thead className="text-xs text-gray-500 bg-white border-b border-gray-200" data-id="element-578">
                <tr data-id="element-579">
                  <th className="py-3 px-4 w-12" data-id="element-580">
                    <input type="checkbox" checked={selectedStates.size === TABLE_DATA.length} onChange={toggleAll} className="rounded border-gray-300 text-teal-600 focus:ring-teal-500" data-id="element-581" />
                  </th>
                  <th className="py-3 px-4 font-medium cursor-pointer hover:text-gray-700" data-id="element-582">
                    <div className="flex items-center" data-id="element-583">
                      State <ArrowUpDown size={12} className="ml-1" data-id="element-584" />
                    </div>
                  </th>
                  <th className="py-3 px-4 font-medium cursor-pointer hover:text-gray-700 text-right" data-id="element-585">
                    <div className="flex items-center justify-end" data-id="element-586">
                      Total HH # <ArrowUpDown size={12} className="ml-1" data-id="element-587" />
                    </div>
                  </th>
                  <th className="py-3 px-4 font-medium cursor-pointer hover:text-gray-700 text-right" data-id="element-588">
                    <div className="flex items-center justify-end" data-id="element-589">
                      Customers <ArrowUpDown size={12} className="ml-1" data-id="element-590" />
                    </div>
                  </th>
                  <th className="py-3 px-4 font-medium cursor-pointer hover:text-gray-700 text-right" data-id="element-591">
                    <div className="flex items-center justify-end" data-id="element-592">
                      Avg NW <ArrowUpDown size={12} className="ml-1" data-id="element-593" />
                    </div>
                  </th>
                  <th className="py-3 px-4 font-medium cursor-pointer hover:text-gray-700 text-right" data-id="element-594">
                    <div className="flex items-center justify-end" data-id="element-595">
                      Penetration <ArrowUpDown size={12} className="ml-1" data-id="element-596" />
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-597">
                {TABLE_DATA.map((row, idx) => <tr key={idx} className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${selectedStates.has(row.state) ? 'bg-teal-50/50' : ''}`} data-id="element-598">
                    <td className="py-3 px-4" data-id="element-599">
                      <input type="checkbox" checked={selectedStates.has(row.state)} onChange={() => toggleState(row.state)} className="rounded border-gray-300 text-teal-600 focus:ring-teal-500" data-id="element-600" />
                    </td>
                    <td className="py-3 px-4 font-medium text-gray-900" data-id="element-601">
                      {row.state}
                    </td>
                    <td className="py-3 px-4 text-right text-gray-600" data-id="element-602">
                      {row.totalHH}
                    </td>
                    <td className="py-3 px-4 text-right text-gray-600" data-id="element-603">
                      {row.customers}
                    </td>
                    <td className="py-3 px-4 text-right text-gray-600" data-id="element-604">
                      {row.avgNW}
                    </td>
                    <td className="py-3 px-4 text-right text-gray-600" data-id="element-605">
                      {row.penetration}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>

          <AnimatePresence data-id="element-606">
            {selectedStates.size > 0 && <motion.div initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: 16
          }} className="sticky bottom-4 mt-4 flex items-center justify-between bg-[#2B3648] text-white rounded-xl px-5 py-3 shadow-lg" data-id="element-607">
                <div className="flex items-center space-x-3" data-id="element-608">
                  <span className="text-sm font-medium" data-id="element-609">
                    {selectedStates.size} state
                    {selectedStates.size > 1 ? 's' : ''} selected
                  </span>
                  <button onClick={() => setSelectedStates(new Set())} className="text-sm text-gray-300 hover:text-white underline underline-offset-2 transition-colors" data-id="element-610">
                    Clear
                  </button>
                </div>
                <button onClick={handleGenerateSegment} className="flex items-center bg-teal-500 hover:bg-teal-600 text-white font-medium text-sm px-4 py-2 rounded-lg transition-colors" data-id="element-611">
                  <Layers size={16} className="mr-2" data-id="element-612" /> Generate Segment
                </button>
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>

      <AnimatePresence data-id="element-613">
        {showToast && <motion.div initial={{
        opacity: 0,
        y: 40
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 40
      }} className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-[#2B3648] text-white px-5 py-3 rounded-xl shadow-xl flex items-center space-x-3" data-id="element-614">
            <Loader2 size={18} className="animate-spin text-teal-400" data-id="element-615" />
            <span className="text-sm font-medium" data-id="element-616">Segment generating...</span>
          </motion.div>}
      </AnimatePresence>
    </div>;
}